import { useCallback, useEffect, useState } from "react";

export const RESEND_COOLDOWN_SECONDS = 60;

interface UseResendCooldownResult {
  /** Seconds left before the action can be retried; `0` once the cooldown elapses. */
  secondsRemaining: number;
  /** Whether the cooldown is currently active (i.e. `secondsRemaining > 0`). */
  isCoolingDown: boolean;
  /** (Re)starts the cooldown from `cooldownSeconds`. */
  startCooldown: () => void;
  /** Clears the cooldown immediately. */
  clearCooldown: () => void;
}

/**
 * Drives a countdown used to rate-limit a repeatable action (e.g. the "Resend
 * code" button on the email OTP screen). Call `startCooldown`
 * after a successful send to begin (or restart) the window.
 *
 * The countdown is anchored to an absolute end timestamp and recomputed from the
 * wall clock on every tick (and whenever the tab becomes visible again). This
 * keeps it accurate even when the browser throttles or pauses timers in a
 * backgrounded tab — returning to the tab snaps it to the correct value rather
 * than resuming from where it froze.
 */
export function useResendCooldown(
  cooldownSeconds: number = RESEND_COOLDOWN_SECONDS
): UseResendCooldownResult {
  // Absolute time (ms epoch) when the cooldown ends; `undefined` when inactive.
  const [endsAt, setEndsAt] = useState<number | undefined>();
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  useEffect(() => {
    if (endsAt === undefined) {
      return undefined;
    }

    const target = endsAt;

    function tick(): void {
      const remaining = Math.max(0, Math.ceil((target - Date.now()) / 1000));
      setSecondsRemaining(remaining);
      if (remaining <= 0) {
        setEndsAt(undefined);
      }
    }

    // Recompute immediately so returning to a throttled/backgrounded tab snaps
    // to the right value without waiting for the next interval tick.
    tick();
    const interval = setInterval(tick, 1000);
    document.addEventListener("visibilitychange", tick);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [endsAt]);

  const startCooldown = useCallback(() => {
    setEndsAt(Date.now() + cooldownSeconds * 1000);
    setSecondsRemaining(cooldownSeconds);
  }, [cooldownSeconds]);

  const clearCooldown = useCallback(() => {
    setEndsAt(undefined);
    setSecondsRemaining(0);
  }, []);

  return {
    secondsRemaining,
    isCoolingDown: secondsRemaining > 0,
    startCooldown,
    clearCooldown,
  };
}
